import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon, 
  XMarkIcon
} from '@heroicons/react/24/outline';
import { getEmailStatusHistory, type EmailStatus } from '../../lib/email';

export default function EmailStatusMonitor() {
  const [statusHistory, setStatusHistory] = useState<EmailStatus[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState<'all' | 'success' | 'failed'>('all');

  useEffect(() => {
    // Initial load
    setStatusHistory(getEmailStatusHistory());

    // Listen for status updates
    const handleStatusUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<EmailStatus>;
      setStatusHistory(prev => [customEvent.detail, ...prev.slice(0, 99)]);
    };

    window.addEventListener('emailStatusUpdate', handleStatusUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('emailStatusUpdate', handleStatusUpdate);
    };
  }, []);

  const filteredHistory = statusHistory.filter(status => {
    if (filter === 'all') return true;
    if (filter === 'success') return status.success;
    if (filter === 'failed') return !status.success;
    return true;
  });

  const displayedHistory = showAll ? filteredHistory : filteredHistory.slice(0, 5);

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-golf-gray flex items-center">
          <EnvelopeIcon className="w-5 h-5 mr-2 text-golf-orange" />
          Email Status Monitor
        </h2>
        <div className="flex items-center space-x-2">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-sm ${
                filter === 'all' 
                  ? 'bg-golf-orange text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('success')}
              className={`px-3 py-1 text-sm ${
                filter === 'success' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Success
            </button>
            <button
              onClick={() => setFilter('failed')}
              className={`px-3 py-1 text-sm ${
                filter === 'failed' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Failed
            </button>
          </div>
          <button
            onClick={() => setStatusHistory(getEmailStatusHistory())}
            className="p-2 text-gray-500 hover:text-golf-orange hover:bg-golf-orange/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {statusHistory.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <EnvelopeIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No email activity recorded yet</p>
          <p className="text-sm mt-2">Email status will appear here when emails are sent</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            {displayedHistory.map((status, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`p-4 rounded-lg border ${
                  status.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {status.success ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`font-medium ${status.success ? 'text-green-800' : 'text-red-800'}`}>
                          {status.success ? 'Email Sent Successfully' : 'Email Failed'}
                        </p>
                        <span className="text-xs text-gray-500">
                          {formatTime(status.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        To: {status.recipient}
                      </p>
                      {status.subject && (
                        <p className="text-sm text-gray-600">
                          Subject: {status.subject}
                        </p>
                      )}
                      <p className="text-sm mt-1 font-medium">
                        {status.message}
                      </p>
                      {status.error && (
                        <details className="mt-2">
                          <summary className="text-xs cursor-pointer text-gray-500 hover:text-gray-700">
                            View error details
                          </summary>
                          <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                            {JSON.stringify(status.error, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredHistory.length > 5 && (
            <div className="text-center">
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-golf-orange hover:text-golf-green text-sm font-medium"
              >
                {showAll ? 'Show Less' : `Show All (${filteredHistory.length})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}